import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { changePassword, getCurrentUser, getNotifications, loadRememberedCredentials, markNotificationRead, saveRememberedCredentials, type AdminNotification } from "../../../services/auth";
import { clearToken, getToken } from "../../../services/api";
import { formatBeijingDateTime } from "../../../utils/datetime";
import RIcon from "../../../components/RIcon";

interface TopBarProps {
  collapsed: boolean;
  onToggleSidebar: () => void;
  onMobileMenu: () => void;
  user?: UserInfo | null;
}

const breadcrumbMap: Record<string, string> = {
  "/workbench": "工作台",
  "/workbench/ai-chat": "AI智能问答",
  "/workbench/doc-gen": "智能文档生成",
  "/workbench/files": "文件管理",
  "/workbench/projects": "项目中心",
  "/workbench/admin/users": "用户管理",
  "/workbench/admin/roles": "角色管理",
  "/workbench/admin/departments": "部门管理",
  "/workbench/admin/jobs": "工种管理",
  "/workbench/admin/menus": "菜单管理",
};

// ─── User Info ───────────────────────────────────────────────────────────────

interface UserInfo {
  id: number;
  username: string;
  email: string | null;
  full_name: string | null;
  department: string | null;
  job_title: string | null;
  role_name: string | null;
  is_superuser: boolean;
}

async function fetchCurrentUser(): Promise<UserInfo | null> {
  const token = getToken();
  if (!token) return null;
  try {
    return await getCurrentUser();
  } catch { return null; }
}

// ─── Profile Modal ────────────────────────────────────────────────────────────

interface ProfileModalProps {
  onClose: () => void;
}

function ProfileModal({ onClose, user }: ProfileModalProps & { user: UserInfo | null }) {
  const avatarChar = (user?.full_name || user?.username || "用").charAt(0);

  const fields = [
    { label: "用户名", value: user?.username },
    { label: "真实姓名", value: user?.full_name },
    { label: "邮箱", value: user?.email },
    { label: "部门", value: user?.department },
    { label: "角色", value: user?.role_name || (user?.is_superuser ? "超级管理员" : null) },
  ];

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
      <div className="bg-white rounded-2xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="text-base font-semibold text-[#1A2B3C]">个人信息</h3>
          <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer">
            <RIcon name="close-line" className="text-[#718096]" />
          </button>
        </div>

        {/* Avatar */}
        <div className="px-6 pt-5 pb-4 flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-[#0EA5A0] flex items-center justify-center text-white text-xl font-bold">{avatarChar}</div>
          <div>
            <p className="text-sm font-semibold text-[#1A2B3C]">{user?.full_name || user?.username || "-"}</p>
            <p className="text-xs text-[#718096]">{[user?.role_name, user?.department].filter(Boolean).join(" · ") || "-"}</p>
          </div>
        </div>

        {/* Info */}
        <div className="px-6 pb-5 space-y-3">
          {fields.map(({ label, value }) => (
            <div key={label} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
              <span className="text-sm text-[#718096]">{label}</span>
              <span className="text-sm text-[#1A2B3C] font-medium">{value || "-"}</span>
            </div>
          ))}
          <div className="flex items-center justify-between py-2">
            <span className="text-sm text-[#718096]">状态</span>
            <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium">正常</span>
          </div>

          <div className="pt-2">
            <button onClick={onClose} className="w-full h-9 border border-gray-200 text-[#4A5568] rounded-lg text-sm hover:bg-gray-50 cursor-pointer whitespace-nowrap">关闭</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Change Password Modal ────────────────────────────────────────────────────

interface PasswordModalProps {
  onClose: () => void;
  username?: string | null;
}

function PasswordModal({ onClose, username }: PasswordModalProps) {
  const [form, setForm] = useState({ oldPwd: "", newPwd: "", confirmPwd: "" });
  const [errors, setErrors] = useState<Partial<typeof form>>({});
  const [success, setSuccess] = useState(false);
  const [saving, setSaving] = useState(false);
  const oldPwdRef = useRef<HTMLInputElement>(null);
  const newPwdRef = useRef<HTMLInputElement>(null);

  const strengthScore = (pwd: string) => {
    let score = 0;
    if (pwd.length >= 8) score++;
    if (/[A-Z]/.test(pwd)) score++;
    if (/[0-9]/.test(pwd)) score++;
    if (/[^a-zA-Z0-9]/.test(pwd)) score++;
    return score;
  };

  const strengthLabel = ["", "弱", "中等", "强", "非常强"];
  const strengthColor = ["", "bg-red-400", "bg-amber-400", "bg-[#0EA5A0]", "bg-emerald-500"];
  const score = strengthScore(form.newPwd);

  const validate = () => {
    const e: Partial<typeof form> = {};
    if (!form.oldPwd) e.oldPwd = "请输入当前密码";
    if (form.newPwd.length < 8) e.newPwd = "新密码至少8位";
    if (form.newPwd !== form.confirmPwd) e.confirmPwd = "两次输入的密码不一致";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      await changePassword(form.oldPwd, form.newPwd);
      const remembered = await loadRememberedCredentials();
      if (username && remembered.username === username) {
        await saveRememberedCredentials(username, form.newPwd);
      }
      setSuccess(true);
      setTimeout(() => { setSuccess(false); onClose(); }, 1500);
    } catch (error) {
      const msg = error instanceof Error ? error.message : "修改密码失败";
      // "新旧密码相同" 属于新密码本身的问题，光标聚焦到「新密码」输入框让用户直接改
      if (msg.includes("新密码与旧密码相同") || msg.includes("相同")) {
        setForm((prev) => ({ ...prev, newPwd: "", confirmPwd: "" }));
        setErrors({ ...errors, newPwd: msg });
        setTimeout(() => newPwdRef.current?.focus(), 0);
      } else {
        setErrors({ ...errors, oldPwd: msg });
        setTimeout(() => oldPwdRef.current?.focus(), 0);
      }
    } finally {
      setSaving(false);
    }
  };

  if (success) {
    return (
      <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl p-8 max-w-sm w-full mx-4 text-center">
          <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <RIcon name="check-line" className="text-3xl text-emerald-500" />
          </div>
          <h3 className="text-base font-semibold text-[#1A2B3C] mb-1">密码修改成功</h3>
          <p className="text-sm text-[#718096]">下次登录时请使用新密码</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
      <div className="bg-white rounded-2xl w-full max-w-sm mx-4 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="text-base font-semibold text-[#1A2B3C]">修改密码</h3>
          <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer">
            <RIcon name="close-line" className="text-[#718096]" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {/* Old password */}
          <div>
            <label className="text-xs text-[#718096] mb-1 block">当前密码</label>
            <div className="relative">
              <input
                ref={oldPwdRef}
                type="password"
                placeholder="请输入当前密码"
                value={form.oldPwd}
                onChange={(e) => setForm({ ...form, oldPwd: e.target.value })}
                className={`w-full h-10 border rounded-lg px-3 pr-10 text-sm outline-none transition-colors bg-white ${
                  errors.oldPwd ? "border-red-400 focus:border-red-400" : "border-gray-200 focus:border-[#0EA5A0]"
                }`}
              />
            </div>
            {errors.oldPwd && <p className="text-xs text-red-500 mt-1">{errors.oldPwd}</p>}
          </div>

          {/* New password */}
          <div>
            <label className="text-xs text-[#718096] mb-1 block">新密码</label>
            <div className="relative">
              <input
                ref={newPwdRef}
                type="password"
                placeholder="至少8位，含大小写字母和数字"
                value={form.newPwd}
                onChange={(e) => setForm({ ...form, newPwd: e.target.value })}
                className={`w-full h-10 border rounded-lg px-3 pr-10 text-sm outline-none transition-colors bg-white ${
                  errors.newPwd ? "border-red-400" : "border-gray-200 focus:border-[#0EA5A0]"
                }`}
              />
            </div>
            {/* Strength bar */}
            {form.newPwd && (
              <div className="mt-2">
                <div className="flex gap-1 mb-1">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className={`flex-1 h-1 rounded-full transition-all ${i <= score ? strengthColor[score] : "bg-gray-200"}`} />
                  ))}
                </div>
                <p className="text-xs text-[#718096]">密码强度：<span className="font-medium">{strengthLabel[score] || "极弱"}</span></p>
              </div>
            )}
            {errors.newPwd && <p className="text-xs text-red-500 mt-1">{errors.newPwd}</p>}
          </div>

          {/* Confirm password */}
          <div>
            <label className="text-xs text-[#718096] mb-1 block">确认新密码</label>
            <div className="relative">
              <input
                type="password"
                placeholder="再次输入新密码"
                value={form.confirmPwd}
                onChange={(e) => setForm({ ...form, confirmPwd: e.target.value })}
                className={`w-full h-10 border rounded-lg px-3 pr-10 text-sm outline-none transition-colors bg-white ${
                  errors.confirmPwd ? "border-red-400" : "border-gray-200 focus:border-[#0EA5A0]"
                }`}
              />
            </div>
            {errors.confirmPwd && <p className="text-xs text-red-500 mt-1">{errors.confirmPwd}</p>}
          </div>

          <div className="flex gap-3 pt-1">
            <button onClick={onClose} className="flex-1 h-10 border border-gray-200 text-[#4A5568] rounded-lg text-sm hover:bg-gray-50 cursor-pointer whitespace-nowrap">取消</button>
            <button onClick={handleSubmit} disabled={saving} className="flex-1 h-10 bg-[#0EA5A0] hover:bg-[#0D9590] disabled:opacity-60 text-white rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap">
              {saving ? "提交中..." : "确认修改"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── TopBar ───────────────────────────────────────────────────────────────────

export default function TopBar({ collapsed, onToggleSidebar, onMobileMenu, user: initialUser }: TopBarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [notifOpen, setNotifOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [user, setUser] = useState<UserInfo | null>(initialUser ?? null);

  useEffect(() => {
    if (initialUser) {
      setUser(initialUser);
      return;
    }
    fetchCurrentUser().then(setUser);
  }, [initialUser]);

  const displayName = user?.full_name || user?.username || "用户";
  const displayRole = user?.role_name || (user?.is_superuser ? "超级管理员" : "");
  const displayDept = user?.department || "";
  const avatarChar = displayName.charAt(0);

  const handleLogout = () => {
    clearToken();
    navigate("/login");
  };

  const currentPage = breadcrumbMap[location.pathname] || "工作台";

  const [notificationsState, setNotificationsState] = useState<AdminNotification[]>([]);

  const loadNotifications = useCallback(() => {
    getNotifications().then(setNotificationsState).catch(() => {});
  }, []);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications, user?.id]);

  useEffect(() => {
    if (!notifOpen) return;
    loadNotifications();
  }, [notifOpen, loadNotifications]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      loadNotifications();
    }, 30000);
    return () => window.clearInterval(timer);
  }, [loadNotifications]);

  const notifications: { id: number; text: string; detail: string; time: string; read: boolean }[] = notificationsState.map((item) => ({
    id: item.id,
    text: item.title,
    detail: item.content || "",
    time: formatBeijingDateTime(item.created_at),
    read: item.is_read,
  }));
  const unreadCount = notifications.filter((item) => !item.read).length;

  return (
    <>
      <header className="h-14 bg-white border-b border-gray-100 flex items-center px-4 gap-3 flex-shrink-0">
        {/* Sidebar Toggle */}
        <button
          onClick={onToggleSidebar}
          className="hidden lg:flex w-8 h-8 items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
        >
          <RIcon name="menu-unfold-line" className={`${collapsed ? "" : ""} text-[#718096]`} />
        </button>
        <button
          onClick={onMobileMenu}
          className="flex lg:hidden w-8 h-8 items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
        >
          <RIcon name="menu-line" className="text-[#718096]" />
        </button>

        {/* Breadcrumb */}
        <div className="flex items-center gap-1.5 text-sm flex-1 min-w-0">
          <Link to="/workbench/ai-chat" className="text-[#718096] hover:text-[#0EA5A0] whitespace-nowrap hidden sm:block">系统首页</Link>
          <span className="text-gray-300 hidden sm:block">/</span>
          <span className="text-[#1A2B3C] font-medium truncate">{currentPage}</span>
        </div>

        {/* Right Actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Notifications */}
          <div className="relative">
            <button
              onClick={() => { setNotifOpen(!notifOpen); setUserOpen(false); }}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 relative cursor-pointer"
            >
              <RIcon name="notification-3-line" className="text-[#718096] text-sm" />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
                  {unreadCount}
                </span>
              )}
            </button>
            {notifOpen && (
              <div className="absolute right-0 top-10 w-[calc(100vw-2rem)] max-w-[320px] bg-white border border-gray-100 rounded-xl z-50 overflow-hidden shadow-xl">
                <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                  <span className="text-sm font-semibold text-[#1A2B3C]">消息通知</span>
                  {notifications.length > 0 && <span className="text-[11px] text-[#718096]">未读 {unreadCount}</span>}
                </div>
                {notifications.length === 0 ? (
                  <div className="px-4 py-8 text-center">
                    <RIcon name="notification-off-line" className="text-2xl text-gray-300 mb-2 block" />
                    <p className="text-sm text-[#718096]">暂无通知</p>
                  </div>
                ) : (
                  <div className="max-h-80 overflow-y-auto divide-y divide-gray-100">
                    {notifications.map((item) => (
                      <div key={item.id} className={`px-4 py-3 ${item.read ? "bg-white" : "bg-[#F4F8FB]"}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-[#1A2B3C]">{item.text}</p>
                            {item.detail && <p className="text-xs text-[#718096] mt-1 whitespace-pre-wrap">{item.detail}</p>}
                            <p className="text-[11px] text-[#A0AEC0] mt-1">{item.time}</p>
                          </div>
                          {!item.read && (
                            <button
                              onClick={async () => {
                                try {
                                  await markNotificationRead(item.id);
                                  setNotificationsState((prev) => prev.map((row) => row.id === item.id ? { ...row, is_read: true } : row));
                                } catch { /* ignore */ }
                              }}
                              className="text-[11px] text-[#0EA5A0] hover:underline whitespace-nowrap cursor-pointer"
                            >
                              标记已读
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* User Avatar */}
          <div className="relative">
            <button
              onClick={() => { setUserOpen(!userOpen); setNotifOpen(false); }}
              className="flex items-center gap-2 px-2 h-8 rounded-lg hover:bg-gray-100 cursor-pointer"
            >
              <div className="w-6 h-6 rounded-full bg-[#0EA5A0] flex items-center justify-center text-white text-xs font-bold">{avatarChar}</div>
              <span className="text-sm text-[#1A2B3C] hidden sm:block">{displayName}</span>
              <RIcon name="arrow-down-s-line" className="text-[#718096] text-xs hidden sm:block" />
            </button>
            {userOpen && (
              <div className="absolute right-0 top-10 w-48 bg-white border border-gray-100 rounded-xl z-50 overflow-hidden">
                {/* User info header */}
                <div className="px-4 py-3 border-b border-gray-100 bg-[#F4F8FB]">
                  <p className="text-xs font-semibold text-[#1A2B3C]">{displayName}</p>
                  <p className="text-xs text-[#718096] mt-0.5">{[displayRole, displayDept].filter(Boolean).join(" · ") || "用户"}</p>
                </div>
                <button
                  onClick={() => { setUserOpen(false); setProfileOpen(true); }}
                  className="w-full flex items-center gap-2.5 px-4 py-2.5 hover:bg-gray-50 text-sm text-[#4A5568] cursor-pointer"
                >
                  <RIcon name="user-settings-line" className="text-[#718096]" />
                  个人设置
                </button>
                <button
                  onClick={() => { setUserOpen(false); setPasswordOpen(true); }}
                  className="w-full flex items-center gap-2.5 px-4 py-2.5 hover:bg-gray-50 text-sm text-[#4A5568] cursor-pointer"
                >
                  <RIcon name="lock-password-line" className="text-[#718096]" />
                  修改密码
                </button>
                <div className="border-t border-gray-100">
                  <button onClick={handleLogout} className="w-full flex items-center gap-2.5 px-4 py-2.5 hover:bg-gray-50 text-sm text-red-500 cursor-pointer">
                    <RIcon name="logout-box-line" />
                    退出登录
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Click outside to close */}
        {(notifOpen || userOpen) && (
          <div className="fixed inset-0 z-40" onClick={() => { setNotifOpen(false); setUserOpen(false); }} />
        )}
      </header>

      {/* Modals */}
      {profileOpen && <ProfileModal onClose={() => setProfileOpen(false)} user={user} />}
      {passwordOpen && <PasswordModal onClose={() => setPasswordOpen(false)} username={user?.username} />}
    </>
  );
}
